/**
 * Anzeigelogik der drei vom `EnvironmentalCalculator` gelieferten Brennstoffspalten
 * (thermischer Energieeinsatz, Brennstoffasche, biogene CO2-Emissionen, Phase 30): Identitaets-
 * suche, Zellenformatierung und Zeilensignatur als reine Funktionen ausserhalb der Komponente.
 *
 * Reines Datenmodul ohne React-Import -- dasselbe Vorgehen wie `usedWasteCodes.ts` (Phase 26).
 * Es rechnet NICHTS nach: die drei Werte kommen fertig vom Server, dieses Modul sucht die
 * passende Zeile heraus, formatiert sie und verdichtet sie zu einer Vergleichs-Signatur.
 */

import type {
  AlternativeFuelDerivedEntry,
  PrimaryFuelDerivedEntry,
} from '../../../types/reports.js';
import { formatTonnage } from '../../../utils/formatting.js';

/**
 * Sucht in einer moeglicherweise `null`en, sparse Liste die Primaerbrennstoffzeile mit
 * passendem `category` UND `fuelType`. Identitaetssuche statt Positionsindex: die Liste liefert
 * nur Zeilen mit Werten (sparse) und kann in beliebiger Reihenfolge stehen -- ein Zugriff ueber
 * den Katalogindex wuerde bei einer sparse oder umsortierten Liste den falschen Wert liefern.
 */
export function findPrimaryFuelDerived(
  rows: PrimaryFuelDerivedEntry[] | null | undefined,
  category: string,
  fuelType: string,
): PrimaryFuelDerivedEntry | undefined {
  return rows?.find((row) => row.category === category && row.fuelType === fuelType);
}

/**
 * Sucht in der abgeleiteten Sekundaerbrennstoffliste die Zeile mit passender `rowId` (Phase 34:
 * Identitaet ist seit dem Umbau auf freie Zeilen die `rowId`, nicht mehr der Katalogschluessel
 * `fuelType`). Die Liste ist nicht mehr sparse ueber einen Katalog, sondern eine 1:1-Abbildung
 * der Eingabezeilen -- die Suche ueber die `rowId` bleibt trotzdem richtig, weil sie unabhaengig
 * von der Position traegt (Zeilen koennen sich zwischen zwei Ladevorgaengen umsortieren) und
 * weil eine Zeile ohne Treffer (`undefined`) der erwartete Zustand einer frisch angelegten, noch
 * nicht gerechneten Zeile ist -- kein Fehlzustand.
 */
export function findAlternativeFuelDerived(
  rows: AlternativeFuelDerivedEntry[] | null | undefined,
  rowId: string,
): AlternativeFuelDerivedEntry | undefined {
  return rows?.find((row) => row.rowId === rowId);
}

/**
 * Formatiert einen abgeleiteten Zellenwert. Leerer String fuer `null`, `undefined`, Leerstring
 * und nicht-numerische Werte; sonst das Ergebnis von `formatTonnage()`.
 *
 * Bewusst NICHT `formatTonnage` direkt: dessen `'–'` waere in einer noch unbefuellten
 * Brennstoffmatrix eine Aussage ("kein Wert" sieht dann aus wie "ein Strich"), und CONTEXT.md
 * verlangt fuer eine Zeile ohne Eingabe eine leere Zelle. Die Zahlformatierung selbst bleibt bei
 * `formatTonnage`, damit es dafuer genau eine Quelle gibt (keine zweite Rundungs-/
 * Tausendertrennzeichen-Logik hier).
 */
export function formatDerivedCell(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (isNaN(parseFloat(value))) {
    return '';
  }

  return formatTonnage(value);
}

/**
 * Verkettet `thermalEnergy`, `fuelAsh` und (falls vorhanden) `biogenicCo2` einer abgeleiteten
 * Zeile zu einem String. `undefined`/`null` (Zeile fehlt bzw. Feld fehlt) werden zum Leerstring.
 *
 * Zweck: zusaetzlicher Vergleichspunkt im `memo()`-Comparator der drei Zeilenkomponenten in
 * `BrennstoffeSection.tsx`, exakt analog zu `errorSig` und `usedWasteCodesSignature`. Ohne ihn
 * aendert sich fuer eine memoisierte Zeile nach der Server-Antwort keiner der verglichenen Props
 * (form ist stabil referenziert, errorSig und idx bleiben gleich) -- die Zeile zeigt dann
 * weiterhin den alten Wert, weil der Comparator die neuen `derived`-Werte gar nicht kennt.
 */
export function fuelRowCalcSig(
  derived: PrimaryFuelDerivedEntry | AlternativeFuelDerivedEntry | undefined,
): string {
  if (!derived) {
    return '';
  }

  const biogenicCo2 = 'biogenicCo2' in derived ? (derived.biogenicCo2 ?? '') : '';

  return [derived.thermalEnergy ?? '', derived.fuelAsh ?? '', biogenicCo2].join('|');
}
